import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import {
  Wind,
  Anchor,
  Activity,
  Camera,
  RefreshCw,
  Mountain,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

/**
 * The Apex Six — the canonical Coach Lewis methodology.
 *
 * Phase 34 (content moat): blurbs are intentionally OUTCOME-FOCUSED rather
 * than mechanism-focused. We name the protocol + when to use it + the
 * payoff. The actual step-by-step execution lives behind the paywall
 * (Locker Room + 1:1). This is the McKinsey 7S pattern: name the
 * framework publicly, execute the framework privately.
 *
 * If you're editing this file: do NOT reintroduce step lists, sequence
 * detail, or specific instructions ("Anchor + Belief + Cue", "Release,
 * Refocus, Re-engage", etc.). Those belong in the IDP, the Locker Room
 * library, and the printable playbook PDF — not on a public marketing
 * page.
 */
const frameworks = [
  {
    n: '01',
    Icon: Wind,
    name: 'The 3-Breath Reset',
    when: 'When pressure spikes mid-game or your mind starts spiraling.',
    blurb: 'Three breaths, under five seconds, on the move — the reset you can run without leaving the play.',
  },
  {
    n: '02',
    Icon: Anchor,
    name: 'The ABC Confidence Stack',
    when: 'Pre-performance — to walk in feeling ready, not nervous.',
    blurb: 'The 90-second ritual that swaps pre-game nerves for pre-game certainty.',
  },
  {
    n: '03',
    Icon: Activity,
    name: 'Green-Yellow-Red Awareness',
    when: 'In-game zone check — to know which protocol to run.',
    blurb: 'The in-game self-scan that tells you which protocol to run — before the wheels come off.',
  },
  {
    n: '04',
    Icon: Camera,
    name: 'The Camera Drill',
    when: 'Visualization — building reps before they happen.',
    blurb: 'How elite athletes build muscle memory for moments they haven\u2019t lived yet.',
  },
  {
    n: '05',
    Icon: RefreshCw,
    name: 'Bounce-Back 3R',
    when: 'Right after a mistake, turnover or missed shot.',
    blurb: 'Recognize, release, refocus — the repeatable way back into the next play, and why most athletes never learn it.',
  },
  {
    n: '06',
    Icon: Mountain,
    name: 'The Performance Pyramid',
    when: 'Pre-game when you feel scattered or rushed.',
    blurb: 'The pre-game sequence that turns scattered into sharp in under 15 minutes.',
  },
];

const fadeIn = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
};

export const ApexSixMethod = ({ calendlyLink }) => {
  return (
    <section
      id="method"
      className="relative py-20 sm:py-24 bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] overflow-hidden"
      data-testid="apex-six-section"
    >
      {/* ambient background */}
      <div className="absolute inset-0 bg-[radial-gradient(700px_circle_at_30%_30%,rgba(0,76,84,0.18),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(500px_circle_at_70%_70%,rgba(79,188,199,0.06),transparent)]" />
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6">
        <motion.div {...fadeIn} className="text-center mb-12 sm:mb-14">
          <div className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#4FBCC7] font-bold mb-3">
            The Method
          </div>
          <h2 className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-white mb-4">
            The Apex Six
          </h2>
          <p className="text-[16px] sm:text-[17px] text-white/65 max-w-[62ch] mx-auto leading-relaxed">
            Six frameworks Coach Lewis runs with serious and elite athletes — the same tools used
            by championship-program players, recruits, and parents who want to support without
            adding pressure. Repeatable. Sport-agnostic. Built for the moments that matter.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-12">
          {frameworks.map((f, i) => {
            const Icon = f.Icon;
            return (
              <motion.article
                key={f.name}
                {...fadeIn}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] hover:border-[#4FBCC7]/30 transition-all duration-300"
                data-testid={`apex-six-card-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-[#004C54]/40 border border-[#004C54]/60 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#4FBCC7]" />
                  </div>
                  <span className="font-mono-accent text-[11px] tracking-[0.18em] text-white/30 font-bold">
                    {f.n}
                  </span>
                </div>
                <h3 className="font-heading text-[17px] sm:text-[18px] font-bold text-white mb-2 leading-snug">
                  {f.name}
                </h3>
                <p className="text-[12.5px] uppercase tracking-[0.08em] font-semibold text-[#4FBCC7]/90 mb-3">
                  {f.when}
                </p>
                <p className="text-[14.5px] text-white/65 leading-relaxed">
                  {f.blurb}
                </p>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          {...fadeIn}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5"
        >
          <Button
            asChild
            className="bg-[#4FBCC7] hover:bg-[#3aa6b1] text-[#0a0d0e] font-bold w-full sm:w-auto h-12 px-7 text-[15px] rounded-xl shadow-xl shadow-[#4FBCC7]/20 active:scale-[0.98] transition-transform"
          >
            <a href="/quiz" data-testid="apex-six-quiz-cta">
              <Sparkles className="mr-2 h-4 w-4" />
              Take the 2-min Mindset Quiz
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="bg-transparent border-2 border-white/25 hover:bg-white/10 hover:border-white/45 text-white font-bold w-full sm:w-auto h-12 px-7 text-[15px] rounded-xl transition-colors"
          >
            <a href={calendlyLink} data-testid="apex-six-book-cta">
              Book a Discovery Call
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </motion.div>
        <p className="text-center text-white/35 text-[12.5px] mt-4">
          Or grab the free preview — <a href="/frameworks" className="underline underline-offset-4 decoration-[#4FBCC7]/40 hover:text-white" data-testid="apex-six-playbook-link">two protocols, fully broken down</a>.
        </p>
      </div>
    </section>
  );
};

export default ApexSixMethod;
